import { useMemo } from 'react';
import { useRouteDetails } from '@lp/features/RoutePlanner/hooks/useRouteDetails';
import { useRouteContext } from '@lp/core/contexts/RouteContext';
import {
  convertAccommodationLegs,
  convertRouteList,
  getDryRunRouteSegments,
  getFilteredRouteLegs,
  getRouteSegments,
} from '@lp/features/RoutePlanner/utils';
import { AccommodationLeg, AviaLegTypes, GroundLegTypes, HubRouteLocation, RouteTypeEnum } from '@/api/apis/route';
import { CaseFacility } from '@/api/apis/case';
import { RouteFilterType, RouteSegmentType } from '@/types/route';

export type ConvertedRouteType = {
  segments: RouteSegmentType[];
  legs: Map<string, (AviaLegTypes | GroundLegTypes)[]>;
  accommodationLegs: Map<string, AccommodationLeg[]>;
};

type UseConvertedRouteParams = {
  routeType: RouteTypeEnum;
  filterType: RouteFilterType;
  donorHospital: CaseFacility;
  txCenter?: CaseFacility;
  donorHospitalDateTimeString?: string | null;
  recipientHospitalDateTimeString?: string | null;
  donorOperationEndDateTimeString?: string | null;
  recipientOperationEndDateTimeString?: string | null;
};

export const useConvertedRoute = ({
  routeType,
  filterType,
  donorHospital,
  txCenter,
  donorHospitalDateTimeString,
  recipientHospitalDateTimeString,
  donorOperationEndDateTimeString,
  recipientOperationEndDateTimeString,
}: UseConvertedRouteParams): ConvertedRouteType => {
  const route = useRouteDetails(routeType);
  const { locationById } = useRouteContext();

  return useMemo(() => {
    const routeLegs = getFilteredRouteLegs(route.legs, filterType) as (AviaLegTypes | GroundLegTypes)[];

    const defaultSegments =
      routeType === RouteTypeEnum.Main
        ? getRouteSegments({
            donorHospital,
            txCenter,
            legs: routeLegs,
            donorHospitalDateTimeString,
            recipientHospitalDateTimeString,
            donorOperationEndDateTimeString,
            recipientOperationEndDateTimeString,
            departureHub: locationById.get(route.departureHub?.locationId ?? '') as HubRouteLocation,
            arrivalHub: locationById.get(route.arrivalHub?.locationId ?? '') as HubRouteLocation,
          })
        : getDryRunRouteSegments({
            donorHospital,
            donorHospitalDateTimeString,
            legs: routeLegs,
            arrivalHub: locationById.get(route.arrivalHub?.locationId ?? '') as HubRouteLocation,
          });

    const { segments: sortedSegments, legs } = convertRouteList(defaultSegments, routeLegs, filterType);
    const filteredAccommodationLegs = getFilteredRouteLegs(route.accommodationLegs, filterType) as AccommodationLeg[];
    const { segments, accommodationLegs } = convertAccommodationLegs(sortedSegments, filteredAccommodationLegs);

    return { segments, legs, accommodationLegs };
  }, [
    routeType,
    route,
    filterType,
    donorHospital,
    txCenter,
    donorHospitalDateTimeString,
    recipientHospitalDateTimeString,
    donorOperationEndDateTimeString,
    recipientOperationEndDateTimeString,
    locationById,
  ]);
};
